import { useState, useEffect, useCallback } from 'react';
import { getProducts } from '../services/products';
import { logError } from '../utils/errorHandler';

/**
 * Custom hook for fetching and managing products
 * @param {Object} options - Hook options
 * @param {boolean} options.autoFetch - Whether to automatically fetch on mount (default: true)
 * @param {Object} options.filters - Optional filters to apply (e.g., { is_active: true })
 * @returns {Object} - Products state and functions
 */
const useProducts = ({ autoFetch = true, filters = {} } = {}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Fetch products from the API
   */
  const fetchProducts = useCallback(async (customFilters = {}) => {
    try {
      setError(null);

      // If this is a refresh, set isRefreshing instead of loading
      if (products.length > 0) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      const mergedFilters = { ...filters, ...customFilters };
      const data = await getProducts(mergedFilters);

      // Normalize variants: map price_dollars -> price, sort by bag size ascending
      const UNIT_LBS = { '18 oz': 1.125, '1 lb': 1, '2 lb': 2, '5 lb': 5, '10 lb': 10, '30 lb': 30, '50 lb': 50 };
      const normalized = (Array.isArray(data) ? data : (data.results || [])).map(product => ({
        ...product,
        variants: product.variants
          ?.map(v => ({ ...v, price: v.price_dollars }))
          .sort((a, b) => (UNIT_LBS[a.unit] ?? 0) - (UNIT_LBS[b.unit] ?? 0)) || [],
      }));
      setProducts(normalized);
    } catch (err) {
      logError('Fetch products', err);
      setError(err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [filters, products.length]);

  /**
   * Refresh products (re-fetch from API)
   */
  const refresh = useCallback(() => {
    return fetchProducts();
  }, [fetchProducts]);

  /**
   * Get a single product by ID from the cached products
   */
  const getProductById = useCallback((productId) => {
    return products.find(p => p.id === productId);
  }, [products]);

  /**
   * Get a product variant by ID
   */
  const getVariantById = useCallback((variantId) => {
    for (const product of products) {
      const variant = product.variants?.find(v => v.id === variantId);
      if (variant) {
        return { ...variant, product };
      }
    }
    return null;
  }, [products]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-fetch products on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetchProducts();
    }
  }, []); // Only run on mount

  return {
    products,
    loading,
    error,
    isRefreshing,
    fetchProducts,
    refresh,
    getProductById,
    getVariantById,
    clearError,
  };
};

export default useProducts;
